type CoaOption = {
  path?: string;
  jobPath?: string;
  productPath?: string;
};

// export type CoaOption = {
//   id: string;
//   label: string;
//   path: string;
//   jobPath?: string;
//   productPath?: string;
// };

// export const coaOptions: CoaOption[] = [
//   { id: 'none', label: 'No COA', path: '' },
//   {
//     id: 'mock',
//     label: 'Mock COA (Blue Dream)',
//     path: '/mock/mock-coa.json',
//     jobPath: '/mock/mock-job.json',
//     productPath: '/mock/mock-product.json',
//   },
//   {
//     id: 'nj',
//     label: 'NJ COA (Alpine Strawberry)',
//     path: '/mock/nj-coa.json',
//   },
// ];

export type MockDataBundle = {
  coa: Record<string, any>;
  job: Record<string, any>;
  product: Record<string, any>;
  coaFlattened: Record<string, any>;
};
 //Convert a snake_case string to camelCase.
export const snakeToCamelCase = (s: string): string =>
  s.replace(/_([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());

const normalizeObjectKeys = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    const camelKey = snakeToCamelCase(key);
    //ff the camelCase key already exists in the original object, prefer it
    if (camelKey !== key && camelKey in obj) {
      result[key] = obj[key];
      continue;
    }
    result[camelKey] = obj[key];
  }
  return result;
};

export const normalizeCoaKeys = (coa: Record<string, any>): Record<string, any> => {
  if (!coa || typeof coa !== 'object') return coa;

  let changed = false;
  const out: Record<string, any> = { ...coa };

  if (coa.ingredients && typeof coa.ingredients === 'object' && !Array.isArray(coa.ingredients)) {
    const normalized = normalizeObjectKeys(coa.ingredients);
    if (Object.keys(normalized).some((k, i) => k !== Object.keys(coa.ingredients)[i])) {
      out.ingredients = normalized;
      changed = true;
    }
  }

  if (coa.terpenes && typeof coa.terpenes === 'object' && !Array.isArray(coa.terpenes)) {
    const normalized = normalizeObjectKeys(coa.terpenes);
    if (Object.keys(normalized).some((k, i) => k !== Object.keys(coa.terpenes)[i])) {
      out.terpenes = normalized;
      changed = true;
    }
  }

  // Mirror totalTerpenes / otherTerpenes from terpenes to ingredients
  // so legacy templates resolve {{coa.ingredients.<key>.percent}}.
  const terpenesSrc = out.terpenes as Record<string, any> | undefined;
  if (terpenesSrc) {
    for (const key of ['totalTerpenes', 'otherTerpenes'] as const) {
      const slug = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      const tpKey = Object.keys(terpenesSrc).find(
        (k) => k.toLowerCase().replace(/[^a-z0-9]/g, '') === slug,
      );

      if (!tpKey) continue;

      const ingr = out.ingredients as Record<string, any> | undefined;
      const existingKey =
        ingr && Object.keys(ingr).find((k) => k.toLowerCase().replace(/[^a-z0-9]/g, '') === slug);
      const cloned = deepClone(terpenesSrc[tpKey]);
      const existingValue = existingKey ? ingr![existingKey] : undefined;

      // Skip if the canonical ingredients entry is already structurally identical
      // and there's no differently-cased duplicate to clean up.
      if (
        existingKey === key &&
        shallowEqual(existingValue, cloned)
      ) {
        continue;
      }

      const next = { ...(ingr || {}) };
      if (existingKey && existingKey !== key) delete next[existingKey];
      next[key] = cloned;
      out.ingredients = next;
      changed = true;
    }
  }

  return changed ? out : coa;
};

/**
 * Deep clones a value. Falls back to JSON round-trip if structuredClone is unavailable.
 */
const deepClone = <T>(value: T): T => {
  if (value === null || typeof value !== 'object') return value;
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

/**
 * Shallow equality check for plain objects. Returns true if both have the same keys
 * and the same primitive values (reference-equality for nested objects).
 */
const shallowEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};

/**
 * Flattens a nested object into dot-notation keys.
 * Example: { foo: { bar: 1 } } => { 'foo.bar': 1 }
 */
export const flattenObject = (
  obj: Record<string, any>,
  prefix = '',
  result: Record<string, any> = {}
): Record<string, any> => {
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      flattenObject(value, newKey, result);
    } else {
      result[newKey] = value;
    }
  }
  return result;
};

/**
 * Transforms raw COA JSON into a data map with nested structure under 'coa' key.
 * This allows valueForKeyPath to traverse paths like 'coa.manufacturer.licenseNumber'.
 */
export const transformCoaToDataMap = (rawCoa: Record<string, any>): Record<string, any> => {
  return { coa: rawCoa };
};

/**
 * @deprecated Legacy function for loading mock COA data from static JSON files.
 * COA data is now fetched from the /package/coa API endpoint.
 * 
 * Loads a COA from a URL and transforms it to a data map
 */
export const loadCoaData = async (path: string): Promise<Record<string, any>> => {
  if (!path) return {};

  try {
    const res = await fetch(path);
    if (!res.ok) {
      console.error(`Failed to load COA: ${res.status} ${res.statusText}`);
      return {};
    }
    const rawCoa = await res.json();
    return transformCoaToDataMap(rawCoa);
  } catch (err) {
    console.error('Failed to load COA data', err);
    return {};
  }
};

/**
 * Loads JSON from a URL, returns empty object on failure
 */
const loadJson = async (path?: string): Promise<Record<string, any>> => {
  if (!path) return {};
  try {
    const res = await fetch(path);
    if (!res.ok) return {};
    return await res.json();
  } catch {
    return {};
  }
};

/**
 * Loads all mock data (COA, job, product) for a given COA option.
 * Data is wrapped under respective keys (coa, job, product) to enable
 * valueForKeyPath traversal of paths like 'coa.manufacturer.licenseNumber'.
 */
export const loadMockDataBundle = async (option: CoaOption): Promise<MockDataBundle> => {
  const [coa, job, product] = await Promise.all([
    loadJson(option.path),
    loadJson(option.jobPath),
    loadJson(option.productPath),
  ]);

  return {
    coa,
    job,
    product,
    coaFlattened: {
      ...(option.path ? { coa } : {}),
      ...(option.jobPath ? { job } : {}),
      ...(option.productPath ? { product } : {}),
    },
  };
};
